"use client";

/**
 * Contador de renders por componente, para identificar un LAZO DE RENDERS en
 * producción.
 *
 * Existe por un caso concreto: `Minified React error #185` ("Maximum update
 * depth exceeded") reportado desde la máquina del profe, que no se reproduce en
 * desarrollo. Ese error lo tira el planificador de React, así que **no trae
 * stack de componentes** — ni siquiera un error boundary de clase lo puede
 * atribuir. Y sin saber QUÉ componente entra en lazo, sólo queda auditar a
 * ciegas.
 *
 * La idea es simple: cada componente sospechoso suma uno cada vez que se
 * renderiza, y la pantalla de error muestra el ranking. En una sesión normal
 * estos números son de dos cifras; el que esté en lazo va a tener miles. Eso
 * señala al culpable sin ambigüedad.
 *
 * El costo es un incremento en un `Map` por render: despreciable frente a
 * cualquier trabajo de layout. Se cuenta por NOMBRE, no por instancia, así que
 * los componentes que se repiten (una `FitLines` por ejercicio) suman entre
 * todos — que es justo lo que se quiere ver.
 */

/**
 * El contador vive en un global y no en el módulo.
 *
 * Con Fast Refresh (y con cualquier bundle que termine incluyendo el módulo dos
 * veces) puede haber DOS instancias: los componentes suman en una y el lector
 * consulta la otra, que está vacía. Pasó exactamente eso durante la depuración
 * y el ranking daba vacío justo cuando hacía falta. Anclarlo al global lo vuelve
 * único, que es lo que un contador global tiene que ser.
 */
const CLAVE = "__vmRenderTally";
const globalRef = globalThis as unknown as Record<string, Map<string, number> | undefined>;
const tally: Map<string, number> = globalRef[CLAVE] ?? new Map<string, number>();
globalRef[CLAVE] = tally;

/**
 * Se llama DURANTE el render. Es seguro: no toca estado de React, sólo un
 * contador de módulo, así que no puede provocar el lazo que intenta detectar.
 */
export function tallyRender(name: string) {
  tally.set(name, (tally.get(name) ?? 0) + 1);
}

/** Ranking listo para pegar en un reporte. Vacío si no se contó nada. */
export function formatRenderTally(top = 24): string | null {
  if (tally.size === 0) return null;
  const ordered = [...tally.entries()].sort((a, b) => b[1] - a[1]).slice(0, top);
  return ordered.map(([name, count]) => `${name}=${count}`).join(" ");
}

/** Vuelve a cero. Sirve para medir UNA interacción aislada. */
export function resetRenderTally() {
  tally.clear();
}

// En desarrollo se expone en `window` para poder medir desde la consola sin
// esperar a que algo explote: se resetea, se hace UNA interacción y se lee el
// ranking. Un componente que renderiza cientos de veces donde deberían ser
// unidades es el lazo, aunque en esta máquina no llegue a tirar el #185.
if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  (window as unknown as Record<string, unknown>).__renderTally = () => formatRenderTally(20);
  (window as unknown as Record<string, unknown>).__resetRenderTally = resetRenderTally;
}
